import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

// Custom components
import Card from 'components/card/Card';
import ErrorState from 'components/states/ErrorState';
import Loader from 'components/Loader';
import { translate } from '../../../i18n';
import { SupportRequestDto } from '../../../models/support/dto/get-support-requests.dto';
import { tableHeaderTextProps, tableProps, tableRowProps, tableCellProps, tableThProps, tableThFlexProps } from '../../../theme/constants';
import { typography } from 'theme/tokens';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetSupportRequests } from '../hooks/useGetSupportRequests';
import { getRelativeTimeBucket, getSupportStatusColorScheme } from '../utils/support.utils';

const columnHelper = createColumnHelper<SupportRequestDto>();

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);
  const { value, unit } = getRelativeTimeBucket(diffInSeconds);

  const singularKey = `game.time_ago.${unit}`;
  const pluralKey = `game.time_ago.${unit}s`;
  const translatedUnit = value === 1 ? translate(singularKey as any) : translate(pluralKey as any);
  const ago = translate('game.time_ago.ago' as any);

  // Check if we're in French mode (when ago is "il y a")
  if (ago === 'il y a') {
    return `${ago} ${value} ${translatedUnit}`;
  }
  return `${value} ${translatedUnit} ${ago}`;
};

const getReasonLabel = (reason: string) => {
  const reasonMap: Record<string, string> = {
    payment_issue: translate('support.reasons.payment_issue' as any),
    booking_issue: translate('support.reasons.booking_issue' as any),
    account_issue: translate('support.reasons.account_issue' as any),
    technical_issue: translate('support.reasons.technical_issue' as any),
    refund_request: translate('support.reasons.refund_request' as any),
    general_inquiry: translate('support.reasons.general_inquiry' as any),
    feedback: translate('support.reasons.feedback' as any),
    other: translate('support.reasons.other' as any),
  };
  return reasonMap[reason] || reason;
};

export default function SupportRequestsTable() {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequestDto | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const limit = 10;
  const { selectedOrganization } = useSelectedOrganization();

  const { data, isLoading, isInitialLoading, isFetching, isError } = useGetSupportRequests({
    page: currentPage,
    limit,
    organizationId: selectedOrganization?.id,
  });
  // `enabled: !!organizationId`. The `!selectedOrganization` guard below failed
  // safe (complete re-derivation of the single gate), but a store-derived gate
  // is D62's fragile pattern; route the never-issued case through the query's
  // own state instead.
  const readState = classifyRead(isError, isInitialLoading, data);

  // Reset page when organization changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedOrganization?.id]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
  const tertiaryTextColor = useColorModeValue('gray.600', 'gray.300');

  const columns = useMemo(() => [
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.submitted_at' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {formatRelativeTime(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('userName', {
      id: 'userName',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.user' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('userEmail', {
      id: 'userEmail',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.email' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="400">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('userPhone', {
      id: 'userPhone',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.phone_number' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="400">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('reason', {
      id: 'reason',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.reason' as any)}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {getReasonLabel(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('support.status' as any)}
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        return (
          <Badge colorScheme={getSupportStatusColorScheme(status)}>
            {status === 'pending' ? translate('support.pending' as any) : translate('support.resolved' as any)}
          </Badge>
        );
      },
    }),
  ], [textColor]);

  const table = useReactTable({
    data: data?.requests || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowClick = (request: SupportRequestDto) => {
    setSelectedRequest(request);
    onOpen();
  };

  // Prompt for the organization when the read was never issued (no org
  // selected). Previously a `!selectedOrganization` store guard; now driven by
  // the query's own state (D62).
  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Box>
            <Text color={textColor} {...typography.pageTitle}>
              {translate('support.support_requests' as any)}
            </Text>
            <Text color={secondaryTextColor} fontSize="sm">
              {translate('support.click_row_to_view_details' as any)}
            </Text>
          </Box>

          {/* Pagination controls in header */}
          {data?.pagination && (
            <HStack spacing={4}>
              <Text fontSize="sm" color={tertiaryTextColor}>
                {translate('game.page_of')} {currentPage + 1} {translate('game.of')} {data.pagination.totalPages}
                {data.pagination.totalCount > 0 && ` (${data.pagination.totalCount} ${translate('game.total')})`}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={handlePreviousPage}
                  isDisabled={currentPage === 0 || isLoading || isFetching}
                  variant="outline"
                >
                  {translate('game.previous')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={!data.pagination.hasNextPage || isLoading || isFetching}
                  variant="outline"
                >
                  {translate('game.next')}
                </Button>
              </HStack>
            </HStack>
          )}
        </Flex>

        <Box>
          <Table {...tableProps}>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      {...tableThProps}
                      borderColor={borderColor}
                    >
                      <Flex {...tableThFlexProps}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  {...tableRowProps}
                  onClick={() => handleRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      {...tableCellProps}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Loading indicator */}
          {/*
            * `isFetching` alone, not `isLoading || isFetching`. React Query v4
            * pins a DISABLED query at `isLoading: true, isFetching: false`, so
            * the old disjunction was a spinner-forever that only the
            * `never-issued` early return above happens to keep unreachable — a
            * private copy of this read's gate (D62). Over every state that IS
            * reachable here the two are identical.
            */}
          {isFetching && <Loader isLoading={true} variant="spinner" />}

          {/* Error state */}
          {/*
            * `unreadable`, not the raw `error` object. This table renders its
            * rows unconditionally, so a failed BACKGROUND refetch did not blank
            * it — it planted a full error block UNDERNEATH a page of good
            * requests, telling the operator the read failed while they were
            * reading its result (R96-3's shape on an always-rendered table).
            */}
          {readState === 'unreadable' && (
            <ErrorState message={translate('support.error_loading_requests' as any)} />
          )}

          {/* Empty state */}
          {!isFetching && readState !== 'unreadable' && (!data?.requests || data.requests.length === 0) && (
            <Box textAlign="center" py={8}>
              <Text color={secondaryTextColor} fontSize="lg">
                {translate('support.no_requests_found' as any)}
              </Text>
            </Box>
          )}
        </Box>
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{translate('support.request_details' as any)}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedRequest && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={secondaryTextColor}>
                    {translate('support.user' as any)}
                  </Text>
                  <Text fontSize="md">{selectedRequest.userName}</Text>
                  <Text fontSize="sm" color={tertiaryTextColor}>{selectedRequest.userEmail}</Text>
                  {selectedRequest.userPhone && (
                    <Text fontSize="sm" color={tertiaryTextColor}>{selectedRequest.userPhone}</Text>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={secondaryTextColor}>
                    {translate('support.reason' as any)}
                  </Text>
                  <Text fontSize="md">{getReasonLabel(selectedRequest.reason)}</Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={secondaryTextColor}>
                    {translate('support.message' as any)}
                  </Text>
                  <Text fontSize="md" whiteSpace="pre-wrap">
                    {selectedRequest.message}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={secondaryTextColor}>
                    {translate('support.status' as any)}
                  </Text>
                  <Badge colorScheme={getSupportStatusColorScheme(selectedRequest.status)}>
                    {selectedRequest.status === 'pending' ? translate('support.pending' as any) : translate('support.resolved' as any)}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={secondaryTextColor}>
                    {translate('support.submitted_at' as any)}
                  </Text>
                  <Text fontSize="md">{new Date(selectedRequest.createdAt).toLocaleString()}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
